import { Button } from "@/components/ui/button";
import { SelectSearch } from "@/components/select-search";
import { PaymentSection } from "@/features/dash/registrations/components/payment-section";
import { PeriodEditor } from "@/features/dash/registrations/components/period-editor";
import { PriceSection } from "@/features/dash/registrations/components/price-section";
import { PromotionSelector } from "@/features/dash/registrations/components/promotion-selector";
import { AdultForm } from "@/features/dash/registrations/forms/adult-form";
import { ChildForm } from "@/features/dash/registrations/forms/child-form";
import { api } from "@/lib/api";
import { registrationFormSchema, type RegistrationFormData } from "@/schemas/registration-schema";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileText, Loader2, Plus, Trash2 } from "lucide-react";
import type { AdultBase, AgeGroupBase, ChildBase, PaymentAmounts, PromotionBase } from "nautika-types";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { getHttpStatusAndMessage } from "@/lib/network-utils";

// Helper pour formater les noms avec "et" à la française
function formatChildNamesWithEt(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} et ${names[1]}`;
  const allButLast = names.slice(0, -1).join(", ");
  return `${allButLast} et ${names[names.length - 1]}`;
}

// Utility to compute age from birthDate string
function computeAge(birthDate: string | Date): number {
  const birth = new Date(birthDate);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}

export function EditRegistration() {
  const { id } = useParams<{ id: string }>();
  const [selectedAdults, setSelectedAdults] = useState<AdultBase[] | null>(null);
  const [selectedChildren, setSelectedChildren] = useState<ChildBase[] | null>(null);
  const [selectedPeriods, setSelectedPeriods] = useState<{ [childId: string]: string[] } | null>(null);
  const [selectedPromotionId, setSelectedPromotionId] = useState<string | null>(null);
  const [subtotalAmount, setSubtotalAmount] = useState<number | null>(null);
  const [openAdultSheet, setOpenAdultSheet] = useState(false);
  const [openChildSheet, setOpenChildSheet] = useState(false);
  const [editingAdult, setEditingAdult] = useState<AdultBase | undefined>(undefined);
  const [editingChild, setEditingChild] = useState<ChildBase | undefined>(undefined);
  const [paymentDetails, setPaymentDetails] = useState<PaymentAmounts | null>(null);
  // Recherches locales pour Command (filtrage manuel)
  const [adultSearch, setAdultSearch] = useState("");
  const [childSearch, setChildSearch] = useState("");

  // États initiaux dérivés des données serveur

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Récupération des données de l'inscription existante
  const { data: registrationData, isLoading: loadingRegistration } = api.useQuery("get", "/registrations/{id}", {
    params: { path: { id: id! } },
    staleTime: 60000,
  });

  const serverRegistration = useMemo(() => {
    return (registrationData || {}) as {
      adults?: AdultBase[];
      children?: ChildBase[];
      reservations?: Array<{ childId: string; reservations: Array<{ reservation: { periodId: string } }> }>;
      promotions?: Array<{ id: string }>;
      subtotalAmount?: number;
      paymentDetails?: PaymentAmounts;
    };
  }, [registrationData]);

  const initialAdults = useMemo(() => serverRegistration.adults || [], [serverRegistration]);
  const initialChildren = useMemo(() => serverRegistration.children || [], [serverRegistration]);
  const initialPeriods = useMemo(() => {
    const periods: { [childId: string]: string[] } = {};
    (serverRegistration.reservations || []).forEach((childReservation) => {
      periods[childReservation.childId] = childReservation.reservations.map((r) => r.reservation.periodId);
    });
    return periods;
  }, [serverRegistration]);
  const initialPromotionId = useMemo(() => (serverRegistration.promotions && serverRegistration.promotions[0]?.id) || null, [serverRegistration]);
  const initialSubtotalAmount = useMemo(() => serverRegistration.subtotalAmount ?? 0, [serverRegistration]);
  const initialPaymentDetails = useMemo<PaymentAmounts>(
    () => serverRegistration.paymentDetails || { card: 0, ancv: 0, cash: 0, check: 0 },
    [serverRegistration]
  );

  // Valeurs effectives: prioriser les overrides locaux sinon fallback sur les valeurs initiales serveur
  const effectiveSelectedAdults = selectedAdults ?? initialAdults;
  const effectiveSelectedChildren = selectedChildren ?? initialChildren;
  const effectiveSelectedPeriods = selectedPeriods ?? initialPeriods;
  const effectivePromotionId = selectedPromotionId ?? initialPromotionId;
  const effectiveSubtotalAmount = subtotalAmount ?? initialSubtotalAmount;
  const effectivePaymentDetails = paymentDetails ?? initialPaymentDetails;

  const { data: adultsData, isLoading: loadingAdults } = api.useQuery("get", "/registrations/adults/", { staleTime: 60000 });
  const adultList = useMemo(() => adultsData || [], [adultsData]);

  const { data: childrenData, isLoading: loadingChildren } = api.useQuery("get", "/registrations/children/", { staleTime: 60000 });
  const childList = useMemo(() => childrenData || [], [childrenData]);

  // Listes filtrées manuellement pour garder le contrôle sur l'ordre + item de création persistant
  const filteredAdults = useMemo(() => {
    if (!adultSearch.trim()) return adultList;
    const q = adultSearch.toLowerCase();
    return adultList.filter((a: AdultBase) => {
      const full = `${a.firstName || ""} ${a.lastName || ""} ${a.phone || ""}`.toLowerCase();
      return full.includes(q);
    });
  }, [adultList, adultSearch]);

  // filteredChildren déplacé plus bas après la définition de groupMap

  const { data: groupsData } = api.useQuery("get", "/club/age-groups", { staleTime: 60000 });
  const groupMap = useMemo(() => {
    const groupsDataList = groupsData || [];
    const map: Record<string, string> = {};
    groupsDataList.forEach((g: AgeGroupBase) => {
      map[g.id] = g.name;
    });
    return map;
  }, [groupsData]);

  const filteredChildren = useMemo(() => {
    if (!childSearch.trim()) return childList;
    const q = childSearch.toLowerCase();
    return childList.filter((c: ChildBase) => {
      const groupName = c.groupId ? groupMap[c.groupId] || "" : "";
      const full = `${c.firstName || ""} ${c.lastName || ""} ${groupName}`.toLowerCase();
      return full.includes(q);
    });
  }, [childList, childSearch, groupMap]);

  const { data: promotionsData } = api.useQuery("get", "/promotions/", { staleTime: 60000 });
  const promotionList = promotionsData || [];
  const selectedPromotion = promotionList.find((p: PromotionBase) => p.id === effectivePromotionId);

  // Détection de fiche dupliquée (ensemble exact d'enfants), ignorer la fiche courante
  const selectedChildIdsCsv = useMemo(
    () =>
      effectiveSelectedChildren
        .map((c: ChildBase) => c.id)
        .sort()
        .join(","),
    [effectiveSelectedChildren]
  );
  const { data: duplicatesData, isLoading: loadingDuplicates } = api.useQuery("get", "/registrations/duplicates", {
    params: { query: { childIds: selectedChildIdsCsv || undefined, excludeId: id } },
    staleTime: 60_000,
    enabled: effectiveSelectedChildren.length > 0,
  });
  const duplicateRegistration = (duplicatesData && (duplicatesData as Array<{ id: string }>)[0]) || null;
  const hasDuplicateChildrenSet = !!duplicateRegistration;

  // No effect needed: state is initialized once via onSuccess above

  // Fonction pour comparer arrays de strings (optimisée pour les IDs)
  const arraysEqual = useCallback((arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item, index) => item === arr2[index]);
  }, []);

  // Fonction pour comparer les objets de périodes
  const periodsEqual = useCallback(
    (periods1: { [childId: string]: string[] }, periods2: { [childId: string]: string[] }): boolean => {
      const keys1 = Object.keys(periods1);
      const keys2 = Object.keys(periods2);
      if (keys1.length !== keys2.length) return false;

      return keys1.every((key) => {
        const arr1 = periods1[key] || [];
        const arr2 = periods2[key] || [];
        return arraysEqual(arr1, arr2);
      });
    },
    [arraysEqual]
  );

  // Fonction pour comparer les objets de paiement
  const paymentsEqual = useCallback((pay1: PaymentAmounts, pay2: PaymentAmounts): boolean => {
    return pay1.card === pay2.card && pay1.ancv === pay2.ancv && pay1.cash === pay2.cash && pay1.check === pay2.check;
  }, []);

  // Calculer la réduction de promotion
  const promotionDiscount = useMemo(() => {
    if (!selectedPromotion || effectiveSubtotalAmount === 0) return 0;
    if (selectedPromotion.type === "percentage") {
      return (effectiveSubtotalAmount * selectedPromotion.value) / 100;
    } else {
      return selectedPromotion.value / 100;
    }
  }, [selectedPromotion, effectiveSubtotalAmount]);

  // Mutation pour mettre à jour l'inscription
  const updateRegistrationMutation = api.useMutation("patch", "/registrations/{id}", {
    onSuccess: async () => {
      toast.success("Inscription mise à jour avec succès !");
      // Invalider le cache des inscriptions avec refetch immédiat
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["get", "/registrations/"], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["get", "/registrations/{id}", { params: { path: { id } } }], refetchType: "all" }),
      ]);
      // Réinitialiser les états locaux pour forcer l'utilisation des nouvelles données serveur
      setSelectedAdults(null);
      setSelectedChildren(null);
      setSelectedPeriods(null);
      setSelectedPromotionId(null);
      setSubtotalAmount(null);
      setPaymentDetails(null);
      // Rediriger vers les détails de l'inscription
      navigate(`/dash/registrations/${id}`);
    },
    onError: (error) => {
      console.error("Error updating registration:", error);
      const { status, message } = getHttpStatusAndMessage(error);
      if (status === 409 || message?.toLowerCase?.().includes("already reserved")) {
        toast.error("Conflit de réservation", {
          description: "Un enfant est déjà réservé pour une des périodes sélectionnées.",
        });
      } else {
        toast.error("Erreur lors de la mise à jour de l'inscription");
      }
    },
  });

  const handleAdultCreated = useCallback(
    (adult: AdultBase) => {
      if (editingAdult) {
        // Si on était en train d'éditer, mettre à jour l'adulte dans la liste
        setSelectedAdults((prev) => {
          const base = prev ?? effectiveSelectedAdults;
          return base.map((a: AdultBase) => (a.id === adult.id ? adult : a));
        });
      } else {
        // Sinon, ajouter le nouvel adulte à la liste
        setSelectedAdults((prev) => {
          const base = prev ?? effectiveSelectedAdults;
          return [...base, adult];
        });
      }
      // Invalider le cache pour recharger la liste
      queryClient.invalidateQueries({ queryKey: ["get", "/registrations/adults/"] });
      // Réinitialiser l'état d'édition
      setEditingAdult(undefined);
    },
    [editingAdult, effectiveSelectedAdults, queryClient]
  );

  const handleChildCreated = useCallback(
    (child: ChildBase) => {
      if (editingChild) {
        // Si on était en train d'éditer, mettre à jour l'enfant dans la liste
        setSelectedChildren((prev) => {
          const base = prev ?? effectiveSelectedChildren;
          return base.map((c: ChildBase) => (c.id === child.id ? child : c));
        });
      } else {
        // Sinon, ajouter le nouvel enfant à la liste
        setSelectedChildren((prev) => {
          const base = prev ?? effectiveSelectedChildren;
          return [...base, child];
        });
      }
      // Invalider le cache pour recharger la liste
      queryClient.invalidateQueries({ queryKey: ["get", "/registrations/children/"] });
      // Réinitialiser l'état d'édition
      setEditingChild(undefined);
    },
    [editingChild, effectiveSelectedChildren, queryClient]
  );

  const handleEditAdult = (adult: AdultBase) => {
    setEditingAdult(adult);
    setOpenAdultSheet(true);
  };

  const handleEditChild = (child: ChildBase) => {
    setEditingChild(child);
    setOpenChildSheet(true);
  };

  const handleNewAdult = () => {
    setEditingAdult(undefined);
    setOpenAdultSheet(true);
  };

  const handleNewChild = () => {
    setEditingChild(undefined);
    setOpenChildSheet(true);
  };

  // Calculer si les sections peuvent être affichées
  const hasChildren = effectiveSelectedChildren.length > 0;
  const hasSelectedPeriods = Object.values(effectiveSelectedPeriods).some((periods) => periods.length > 0);

  // Calculer les enfants sans périodes
  const childrenWithoutPeriods = useMemo(
    () => effectiveSelectedChildren.filter((child: ChildBase) => !effectiveSelectedPeriods[child.id] || effectiveSelectedPeriods[child.id].length === 0),
    [effectiveSelectedChildren, effectiveSelectedPeriods]
  );

  // Validation du formulaire avec Zod
  const validationResult = useMemo(() => {
    const formData: RegistrationFormData = {
      adults: effectiveSelectedAdults.map((adult: AdultBase) => ({ id: adult.id })),
      children: effectiveSelectedChildren.map((child: ChildBase) => ({ id: child.id })),
      periods: effectiveSelectedPeriods,
      promotionId: effectivePromotionId || undefined,
      price: effectiveSubtotalAmount,
      paymentMethods: effectivePaymentDetails,
    };
    return registrationFormSchema.safeParse(formData);
  }, [effectiveSelectedAdults, effectiveSelectedChildren, effectiveSelectedPeriods, effectivePromotionId, effectiveSubtotalAmount, effectivePaymentDetails]);

  const isFormValid = validationResult.success;
  const formErrors = useMemo(() => (!validationResult.success ? validationResult.error.issues : []), [validationResult.success, validationResult.error]);

  // Prioritize errors: adults > children/duplicate > periods > price
  const prioritizedError = useMemo(() => {
    // 1. Check for adults error
    const adultsError = formErrors.find((e) => e.path.includes("adults"));
    if (adultsError) return adultsError.message;

    // 2. Check for children errors or duplicate registration
    const childrenError = formErrors.find((e) => e.path.includes("children"));
    if (childrenError) return childrenError.message;
    if (hasDuplicateChildrenSet) return null; // Duplicate is shown near children list, not in bottom section

    // 3. Check for periods error
    const periodsError = formErrors.find((e) => e.message.toLowerCase().includes("période"));
    if (periodsError) return periodsError.message;

    // 4. Check for price error
    const priceError = formErrors.find((e) => e.path.includes("price"));
    if (priceError) return priceError.message;

    return null;
  }, [formErrors, hasDuplicateChildrenSet]);

  const duplicateChildNamesList = useMemo(
    () => effectiveSelectedChildren.map((c) => `${c.firstName || ""} ${c.lastName || ""}`.trim()).filter(Boolean),
    [effectiveSelectedChildren]
  );
  const duplicateChildNames = useMemo(() => formatChildNamesWithEt(duplicateChildNamesList), [duplicateChildNamesList]);

  // Fonction pour soumettre le formulaire
  const handleSubmit = () => {
    if (!isFormValid) {
      toast.error("Des informations sont manquantes :", { description: formErrors.map((error) => error.message).join(", ") });
      return;
    }

    // Préparer les données pour l'API - n'envoyer que les champs modifiés (PATCH partiel)
    const registrationData: {
      adultIds?: string[];
      childrenReservations?: { childId: string; periodIds: string[] }[];
      paymentDetails?: PaymentAmounts;
      promotionId?: string;
      subtotalAmount?: number;
    } = {};

    // Comparer les adultes (par ID)
    const currentAdultIds = effectiveSelectedAdults.map((a: AdultBase) => a.id).sort();
    const initialAdultIdsSorted = initialAdults.map((a: AdultBase) => a.id).sort();
    if (!arraysEqual(currentAdultIds, initialAdultIdsSorted)) {
      registrationData.adultIds = effectiveSelectedAdults.map((adult: AdultBase) => adult.id);
    }

    // Comparer les enfants et périodes
    const currentChildIds = effectiveSelectedChildren.map((c: ChildBase) => c.id).sort();
    const initialChildIdsSorted = initialChildren.map((c: ChildBase) => c.id).sort();
    const childrenChanged = !arraysEqual(currentChildIds, initialChildIdsSorted);
    const periodsChanged = !periodsEqual(effectiveSelectedPeriods, initialPeriods);

    if (childrenChanged || periodsChanged) {
      const childrenReservations = effectiveSelectedChildren
        .map((child: ChildBase) => ({
          childId: child.id,
          periodIds: effectiveSelectedPeriods[child.id] || [],
        }))
        .filter((reservation) => reservation.periodIds.length > 0);
      registrationData.childrenReservations = childrenReservations;
    }

    // Comparer les détails de paiement
    if (!paymentsEqual(effectivePaymentDetails, initialPaymentDetails)) {
      registrationData.paymentDetails = effectivePaymentDetails;
    }

    // Comparer la promotion
    if (effectivePromotionId !== initialPromotionId) {
      registrationData.promotionId = effectivePromotionId || undefined;
    }

    // Comparer le montant sous-total
    if (effectiveSubtotalAmount !== initialSubtotalAmount) {
      registrationData.subtotalAmount = effectiveSubtotalAmount;
    }

    // Si aucun changement, ne pas envoyer la requête
    if (Object.keys(registrationData).length === 0) {
      toast.info("Aucune modification détectée");
      return;
    }

    updateRegistrationMutation.mutate({
      params: { path: { id: id! } },
      body: registrationData,
    });
  };

  // Callbacks stabilisés pour éviter les re-renders
  const handlePeriodsChange = useCallback(
    (periods: { [childId: string]: string[] }) => {
      // Avoid setting identical state to prevent update depth loops
      const current = selectedPeriods ?? effectiveSelectedPeriods;
      const keys1 = Object.keys(periods);
      const keys2 = Object.keys(current);
      const sameKeys = keys1.length === keys2.length && keys1.every((k) => keys2.includes(k));
      let equal = sameKeys;
      if (equal) {
        for (const k of keys1) {
          const a = [...(periods[k] || [])].sort();
          const b = [...(current[k] || [])].sort();
          if (a.length !== b.length) {
            equal = false;
            break;
          }
          for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
              equal = false;
              break;
            }
          }
          if (!equal) break;
        }
      }
      if (!equal) setSelectedPeriods(periods);
    },
    [selectedPeriods, effectiveSelectedPeriods]
  );

  const handlePaymentChange = useCallback((payments: PaymentAmounts) => {
    // Normaliser les valeurs undefined en 0 pour être cohérent avec les valeurs initiales
    const normalizedPayments = {
      card: payments.card ?? 0,
      ancv: payments.ancv ?? 0,
      cash: payments.cash ?? 0,
      check: payments.check ?? 0,
    };
    setPaymentDetails(normalizedPayments);
  }, []);

  const handleSubtotalChange = useCallback((subtotal: number) => {
    setSubtotalAmount(subtotal);
  }, []);

  if (loadingRegistration) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="mx-auto max-w-6xl space-y-6 p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-6xl space-y-6 p-6">
        {/* Titre de la section */}
        <div>
          <h2 className="text-2xl font-bold">Modifier l'inscription</h2>
        </div>
        {/* Adults field - toujours affiché */}
        <div className="flex flex-col">
          <p className="mb-2 text-lg font-semibold">Adultes de confiance</p>
          <p className="text-sm text-gray-700">
            Indiquez ici la ou les personnes susceptibles de venir chercher les enfants au club et que vous pourrez contacter en cas de nécessité
          </p>

          {/* SelectSearch pour les adultes */}
          <SelectSearch
            items={filteredAdults}
            selectedItems={effectiveSelectedAdults}
            isLoading={loadingAdults}
            onItemSelect={(adult) => {
              const typedAdult = adult as AdultBase;
              const base = effectiveSelectedAdults;
              const isSelected = base.find((a: AdultBase) => a.id === typedAdult.id);
              if (isSelected) {
                setSelectedAdults(base.filter((a: AdultBase) => a.id !== typedAdult.id));
              } else {
                setSelectedAdults([...base, typedAdult]);
              }
            }}
            onCreateNew={handleNewAdult}
            filterFunction={(items, searchTerm) => {
              const term = searchTerm.trim().toLowerCase();
              if (!term) return items;
              return items.filter((a: AdultBase) => `${a.firstName} ${a.lastName} ${a.phone || ""}`.toLowerCase().includes(term));
            }}
            renderItem={(adult) => {
              const typedAdult = adult as AdultBase;
              return (
                <span>
                  {typedAdult.firstName || "N/A"} {typedAdult.lastName || "N/A"}
                  {typedAdult.phone ? ` (${typedAdult.phone})` : ""}
                </span>
              );
            }}
            renderTrigger={() => (
              <Button variant="outline" className="mt-4 w-[280px] justify-start">
                <Plus className="mr-2" /> Ajouter un adulte de confiance
              </Button>
            )}
            onSearch={setAdultSearch}
            placeholder="Rechercher un adulte..."
            createNewText="Créer un nouvel adulte"
            emptyMessage="Aucun adulte trouvé"
            loadingMessage="Chargement..."
            width="w-[320px]"
            popoverAlign="start"
            popoverSide="bottom"
            showCreateButton={true}
            closeOnSelect={false}
            isMultiSelect={true}
          />
          <div className="mt-2 flex flex-col gap-y-2">
            {effectiveSelectedAdults.map((adult: AdultBase) => {
              return (
                <div key={`selected-adult-${adult.id}`} className={"flex items-center justify-between rounded-md border bg-background p-3"}>
                  <span>
                    {adult.firstName || "N/A"} {adult.lastName || "N/A"}
                    {adult.phone ? ` (${adult.phone})` : ""}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="default" onClick={() => handleEditAdult(adult)} title="Voir la fiche">
                      <FileText className="h-4 w-4 text-blue-500" />
                      <span className="ml-1 text-sm text-blue-500">Voir la fiche</span>
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setSelectedAdults(effectiveSelectedAdults.filter((a) => a.id !== adult.id))}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Children field - toujours affiché */}
        <div>
          <p className="mb-2 text-lg font-semibold">Enfants</p>
          <p className="text-sm text-gray-700">Renseignez ici les enfants concernés par l'inscription</p>
          {/* SelectSearch pour les enfants */}
          <SelectSearch
            items={filteredChildren}
            selectedItems={effectiveSelectedChildren}
            isLoading={loadingChildren}
            onItemSelect={(child) => {
              const typedChild = child as ChildBase;
              const base = effectiveSelectedChildren;
              const isSelected = base.find((c: ChildBase) => c.id === typedChild.id);
              if (isSelected) {
                setSelectedChildren(base.filter((c: ChildBase) => c.id !== typedChild.id));
              } else {
                setSelectedChildren([...base, typedChild]);
              }
            }}
            onCreateNew={handleNewChild}
            filterFunction={(items, searchTerm) => {
              const term = searchTerm.trim().toLowerCase();
              if (!term) return items;
              return items.filter((c: ChildBase) =>
                `${c.firstName} ${c.lastName} ${c.groupId && groupMap[c.groupId] ? groupMap[c.groupId] : ""}`.toLowerCase().includes(term)
              );
            }}
            renderItem={(child) => {
              const typedChild = child as ChildBase;
              return (
                <span>
                  {typedChild.firstName || "N/A"} {typedChild.lastName || "N/A"} · {typedChild.birthDate ? computeAge(typedChild.birthDate) : "N/A"} ans
                  {typedChild.groupId && groupMap[typedChild.groupId] ? <> · {groupMap[typedChild.groupId]}</> : null}
                </span>
              );
            }}
            renderTrigger={() => (
              <Button variant="outline" className="mt-4 w-[280px] justify-start">
                <Plus className="mr-2" /> Ajouter un enfant
              </Button>
            )}
            onSearch={setChildSearch}
            placeholder="Rechercher un enfant..."
            createNewText="Créer un nouvel enfant"
            emptyMessage="Aucun enfant trouvé"
            loadingMessage="Chargement..."
            width="w-[320px]"
            popoverAlign="start"
            popoverSide="bottom"
            showCreateButton={true}
            closeOnSelect={false}
            isMultiSelect={true}
          />
          <div className={"mt-2 flex flex-col gap-y-2"}>
            {effectiveSelectedChildren.map((child: ChildBase) => {
              return (
                <div
                  key={`selected-child-${child.id}`}
                  className={`flex items-center justify-between rounded-md border bg-background p-3 ${hasDuplicateChildrenSet ? "rounded-md border border-red-300 bg-red-50" : ""}`}
                >
                  <span>
                    {child.firstName || "N/A"} {child.lastName || "N/A"} · {child.birthDate ? computeAge(child.birthDate) : "N/A"} ans
                    {child.groupId && groupMap[child.groupId] ? <> · {groupMap[child.groupId]}</> : null}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="default" onClick={() => handleEditChild(child)} title="Voir la fiche">
                      <FileText className="h-4 w-4 text-blue-500" />
                      <span className="ml-1 text-sm text-blue-500">Voir la fiche</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setSelectedChildren(effectiveSelectedChildren.filter((c: ChildBase) => c.id !== child.id))}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          {hasDuplicateChildrenSet && (
            <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium">
                  {duplicateChildNames ? (
                    <>
                      <em>{duplicateChildNames}</em>
                      {effectiveSelectedChildren.length > 1 ? " sont déjà inscrits" : " est déjà inscrit"} sur une autre fiche.
                    </>
                  ) : effectiveSelectedChildren.length > 1 ? (
                    "Une inscription existe déjà pour exactement ces enfants."
                  ) : (
                    "Une inscription existe déjà pour cet enfant."
                  )}
                </p>
                {duplicateRegistration && (
                  <a
                    className="flex items-center gap-2 whitespace-nowrap underline"
                    href={`/dash/registrations/${(duplicateRegistration as { id: string }).id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Voir la fiche
                    <ExternalLink className="size-4" />
                  </a>
                )}
              </div>
              {loadingDuplicates && <p className="mt-1 text-[11px] italic">Vérification en cours...</p>}
            </div>
          )}
        </div>{" "}
        {/* Period selection - toujours affiché */}
        <PeriodEditor
          selectedChildren={effectiveSelectedChildren}
          onPeriodsChange={handlePeriodsChange}
          initialSelectedPeriods={effectiveSelectedPeriods}
          registrationId={id!}
          isInitialLoading={loadingRegistration}
        />
        {hasChildren && childrenWithoutPeriods.length > 0 && (
          <div className="rounded-md border border-dashed border-yellow-200 bg-yellow-50 p-4">
            <p className="mb-2 font-semibold text-yellow-800">Périodes manquantes</p>
            <ul className="space-y-1 text-sm text-yellow-700">
              {childrenWithoutPeriods.map((child: ChildBase) => (
                <li key={child.id}>
                  Aucune période n'a été sélectionnée pour {child.firstName} {child.lastName}
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Prix de l'inscription - affiché seulement si des périodes sont sélectionnées */}
        {hasSelectedPeriods && childrenWithoutPeriods.length === 0 && <PriceSection value={effectiveSubtotalAmount} onPriceChange={handleSubtotalChange} />}
        {/* Promotion selection - affiché seulement si des périodes sont sélectionnées */}
        {hasSelectedPeriods && childrenWithoutPeriods.length === 0 && (
          <PromotionSelector selectedPromotionId={effectivePromotionId} onPromotionChange={setSelectedPromotionId} />
        )}
        {/* Message d'avertissement si des enfants n'ont pas de périodes */}
        {/* Payment section - affiché seulement si des périodes sont sélectionnées */}
        {hasSelectedPeriods && childrenWithoutPeriods.length === 0 && (
          <PaymentSection
            promotionDiscount={promotionDiscount}
            onPaymentChange={handlePaymentChange}
            hideSubtotalColumn
            subtotalValue={effectiveSubtotalAmount}
            initialPayments={initialPaymentDetails}
          />
        )}
        {/* Submit section - affiché seulement si le formulaire peut être soumis */}
        {hasSelectedPeriods && childrenWithoutPeriods.length === 0 && (
          <div className="w-full">
            {/* Liste des erreurs de validation - affiche uniquement l'erreur la plus importante */}
            {prioritizedError && (
              <div className="mb-4 w-full rounded-md border border-dashed border-red-200 bg-red-50 p-4 text-red-700">
                <p>{prioritizedError}</p>
              </div>
            )}
            {/* Bouton de soumission aligné à droite */}
            <div className="mt-20 flex justify-end">
              <Button onClick={handleSubmit} disabled={updateRegistrationMutation.isPending || hasDuplicateChildrenSet} className="mb-48 min-w-64">
                {updateRegistrationMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Mise à jour...
                  </>
                ) : hasDuplicateChildrenSet ? (
                  "Fiche dupliquée détectée"
                ) : (
                  "Mettre à jour cette inscription"
                )}
              </Button>
            </div>
          </div>
        )}
        {/* Adult and Child creation forms */}
        <AdultForm open={openAdultSheet} onOpenChange={setOpenAdultSheet} onAdultCreated={handleAdultCreated} editingAdult={editingAdult} />
        <ChildForm open={openChildSheet} onOpenChange={setOpenChildSheet} onChildCreated={handleChildCreated} editingChild={editingChild} />
      </div>
    </>
  );
}
